import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, ScanSearch, FileText, AlertTriangle, TrendingUp, User, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_STYLE = {
  NONE:     'bg-slate-800 border-slate-600 text-slate-300',
  LOW:      'bg-emerald-950 border-emerald-600 text-emerald-300',
  MODERATE: 'bg-amber-950 border-amber-600 text-amber-300',
  HIGH:     'bg-rose-950 border-rose-600 text-rose-300',
  PENDING:  'bg-slate-900 border-slate-700 text-slate-400',
};

export default function VettingList() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ applicant_name: '', id_number: '', monthly_salary: '', salary_payday: 25, salary_cycle_type: 'monthly', notes: '' });
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/vetting/cases`, { headers });
      setCases(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load cases');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.applicant_name.trim()) return toast.error('Applicant name is required');
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/api/vetting/cases`, form, { headers });
      toast.success('Case created');
      setDialogOpen(false);
      navigate(`/vetting/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create case');
    } finally { setSaving(false); }
  };

  const createSample = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/vetting/cases/sample`, {}, { headers });
      toast.success('Sample case created — click Run Analysis to see the flow.');
      navigate(`/vetting/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sample creation failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vetting-list">
        <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <ScanSearch className="h-7 w-7 sm:h-8 sm:w-8 text-emerald-400" /> 2K2Day Vetting Engine
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-1">Loan detection & borrowing-behaviour assessment from bank statements.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={createSample} variant="outline" className="bg-slate-800 border-slate-700 text-white" data-testid="try-sample-case-btn">
              <Sparkles className="h-4 w-4 mr-1" /> Try Sample Case
            </Button>
            <Button onClick={() => setDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-vetting-case-btn">
              <Plus className="h-4 w-4 mr-1" /> New Case
            </Button>
          </div>
        </div>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader><CardTitle className="text-white text-lg">Vetting Cases</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-slate-400 text-center py-6">Loading…</div>
              : cases.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-40" />
                  <p>No vetting cases yet. Click <span className="text-emerald-400">New Case</span> to start.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-slate-200" data-testid="vetting-cases-table">
                    <thead className="text-xs text-slate-400 border-b border-slate-700">
                      <tr>
                        <th className="text-left py-2 px-2">Applicant</th>
                        <th className="text-left py-2 px-2">ID Number</th>
                        <th className="text-right py-2 px-2">Salary</th>
                        <th className="text-center py-2 px-2">Docs</th>
                        <th className="text-center py-2 px-2">Lenders</th>
                        <th className="text-center py-2 px-2">Stacking</th>
                        <th className="text-center py-2 px-2">Activity</th>
                        <th className="text-center py-2 px-2">Status</th>
                        <th className="text-right py-2 px-2">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map(c => (
                        <tr key={c.id} onClick={() => navigate(`/vetting/${c.id}`)} className="border-b border-slate-800 hover:bg-slate-800/60 cursor-pointer" data-testid={`vetting-case-row-${c.id}`}>
                          <td className="py-2 px-2 font-medium text-white">{c.applicant_name}</td>
                          <td className="py-2 px-2 text-slate-400 font-mono text-xs">{c.id_number || '—'}</td>
                          <td className="py-2 px-2 text-right">R {Number(c.monthly_salary || 0).toLocaleString()}</td>
                          <td className="py-2 px-2 text-center">{(c.documents || []).length}</td>
                          <td className="py-2 px-2 text-center">{c.total_lenders || 0}</td>
                          <td className="py-2 px-2 text-center">
                            {c.loan_stacking ? <AlertTriangle className="h-4 w-4 text-rose-400 inline" /> : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <Badge className={`text-[10px] border ${ACTIVITY_STYLE[c.borrowing_activity] || ACTIVITY_STYLE.PENDING}`}>{c.borrowing_activity}</Badge>
                          </td>
                          <td className="py-2 px-2 text-center text-xs text-slate-400 uppercase">{c.status}</td>
                          <td className="py-2 px-2 text-right text-xs text-slate-500">{c.created_at?.substring(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><User className="h-5 w-5" /> New Vetting Case</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-slate-400 block mb-1">Applicant Name *</label>
              <Input value={form.applicant_name} onChange={e => setForm({ ...form, applicant_name: e.target.value })} data-testid="applicant-name-input" />
            </div>
            <div>
              <label className="text-slate-400 block mb-1">ID Number</label>
              <Input value={form.id_number} onChange={e => setForm({ ...form, id_number: e.target.value })} data-testid="applicant-id-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-slate-400 block mb-1">Monthly Salary (R)</label>
                <Input type="number" value={form.monthly_salary} onChange={e => setForm({ ...form, monthly_salary: e.target.value })} data-testid="applicant-salary-input" />
              </div>
              <div>
                <label className="text-slate-400 block mb-1">Salary Payday (day of month)</label>
                <Input type="number" min="1" max="31" value={form.salary_payday} onChange={e => setForm({ ...form, salary_payday: e.target.value })} data-testid="applicant-payday-input" />
              </div>
            </div>
            <div>
              <label className="text-slate-400 block mb-1">Notes</label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="applicant-notes-input" />
            </div>
            <div className="text-xs text-slate-500 flex items-start gap-1"><TrendingUp className="h-3 w-3 mt-0.5" /> After creating, upload 1–3 months of bank statements (PDF or photos) then run analysis.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-slate-800 border-slate-700 text-white">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-vetting-case-btn">{saving ? 'Creating…' : 'Create Case'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
